import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Button from 'antd/lib/button';
import Radio from 'antd/lib/radio';
import Spin from 'antd/lib/spin';
import Table from 'antd/lib/table';
import { platformLabel } from '../../../account-management/components/AccountDetail';
import PhoneUsageAuditLog from './PhoneUsageAuditLog';
import {
  DISPLAY_TIME_ZONE,
  RESOLUTIONS,
  summarizeView,
  appLabel,
  formatDuration,
  formatDateTimeIn,
  formatRange,
  viewRange,
  bucketLabel,
  bucketEdgeLabel,
  overlappingBuckets,
  countLabel,
  ofLabel,
} from './usageModel';

const VISIBLE_APPS = 8;

const RadioGroup = Radio.Group;
const RadioButton = Radio.Button;

const RESOLUTION_COPY = {
  daily: {
    tab: 'Denné záznamy',
    adjective: 'denné',
    by: 'Po dňoch',
    list: 'Hodnoty po dňoch',
    detail: 'Za každý deň a aplikáciu',
  },
  weekly: {
    tab: 'Týždenné súhrny',
    adjective: 'týždenné',
    by: 'Po týždňoch',
    list: 'Hodnoty po týždňoch',
    detail: 'Jeden súčet za týždeň, bez denných hodnôt',
  },
  monthly: {
    tab: 'Mesačné súhrny',
    adjective: 'mesačné',
    by: 'Po mesiacoch',
    list: 'Hodnoty po mesiacoch',
    detail: 'Jeden súčet za mesiac, bez denných hodnôt',
  },
  yearly: {
    tab: 'Ročné súhrny',
    adjective: 'ročné',
    by: 'Po rokoch',
    list: 'Hodnoty po rokoch',
    detail: 'Jeden súčet za rok, bez denných hodnôt',
  },
};

const OLDER = ['weekly', 'monthly', 'yearly'];

const NO_SNAPSHOT_REASONS = {
  'not-shared': 'Klient pri odoslaní žiadosti nezaškrtol zdieľanie prehľadu.',
  denied: 'Klient zaškrtol zdieľanie, ale v telefóne nepovolil prístup k údajom o používaní.',
  unsupported: 'Telefón alebo jeho systém zachytenie nepodporoval.',
  failed: 'Príprava údajov v telefóne zlyhala a žiadosť sa odoslala bez snímky.',
  skipped: 'Zachytenie sa v telefóne preskočilo alebo zrušilo a žiadosť sa odoslala bez snímky.',
  'not-recorded': 'Pri tejto žiadosti sa voľba zdieľania nezaznamenala, napríklad lebo vznikla pred spustením tejto funkcie.',
};

const NO_SNAPSHOT_FALLBACK = 'Server pri tejto žiadosti neuvádza žiadnu snímku.';

const HISTORY_SOURCE = {
  android: 'Štatistiky používania systému Android',
  ios: 'Screen Time (DeviceActivity) v iOS',
};

export const errorText = error => {
  const status = error && error.status;
  if (status === 401) return 'Prihlásenie vypršalo (HTTP 401).';
  if (status === 403) return 'Server odmietol prístup (HTTP 403).';
  if (status === 404) return 'Pôžička alebo snímka sa na serveri nenašla (HTTP 404).';
  if (status === 'network') return 'Server nie je dostupný.';
  if (status === 'invalid') return 'Server vrátil neočakávanú odpoveď.';
  return `Server vrátil chybu${typeof status === 'number' ? ` (HTTP ${status})` : ''}.`;
};

export const deviceText = device =>
  [device.deviceName, [platformLabel(device.platform), device.osVersion].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');

const PhoneVisual = ({ empty }) => (
  <span className={empty ? 'phone-usage__phone phone-usage__phone--empty' : 'phone-usage__phone'} aria-hidden="true">
    <span className="phone-usage__phone-screen">
      {!empty && <span className="phone-usage__phone-bar" />}
      {!empty && <span className="phone-usage__phone-bar" />}
      {!empty && <span className="phone-usage__phone-bar" />}
    </span>
  </span>
);

PhoneVisual.propTypes = { empty: PropTypes.bool };

const Field = ({ label, children, testId }) => (
  <div className="phone-usage__field" data-testid={testId}>
    <div className="phone-usage__label">{label}</div>
    <div>{children}</div>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  testId: PropTypes.string,
};

const Note = ({ testId, children }) => (
  <div className="phone-usage__hint" role="note" data-testid={testId}>
    {children}
  </div>
);

Note.propTypes = { testId: PropTypes.string, children: PropTypes.node };

export const PhoneUsageFrame = ({ children, sectionRef }) => (
  <section className="phone-usage" aria-labelledby="phone-usage-title" data-testid="phone-usage" ref={sectionRef}>
    <h3 className="phone-usage__title" id="phone-usage-title">
      Používanie aplikácií z telefónu{' '}
      <span className="phone-usage__subtitle">(zachytené raz pri odoslaní žiadosti, nie aktuálne údaje účtu)</span>
    </h3>
    <div className="phone-usage__card">{children}</div>
  </section>
);

PhoneUsageFrame.propTypes = { children: PropTypes.node, sectionRef: PropTypes.func };

const BucketCells = ({ app, view, overlapping, id }) => (
  <ul
    className="phone-usage__days"
    id={id}
    aria-label={`${RESOLUTION_COPY[view.resolution].list}: ${appLabel(app).primary}`}
  >
    {view.buckets.map(bucket => {
      const duration = app.durationByBucket[bucket.key];
      const missing = duration === null || duration === undefined;
      const prefix = view.resolution === 'daily' ? 'phone-usage-day' : 'phone-usage-bucket';
      const edge = bucketEdgeLabel(bucket);
      const classes = ['phone-usage__day'];
      if (missing) classes.push('phone-usage__day--missing');
      if (edge) classes.push('phone-usage__day--edge');
      return (
        <li key={bucket.key} className={classes.join(' ')} data-testid={`${prefix}-${bucket.key}${missing ? '-missing' : ''}`}>
          <span className="phone-usage__day-date">{bucketLabel(view.resolution, bucket)}</span>
          <span className="phone-usage__day-value">{formatDuration(duration)}</span>
          {edge && <span className="phone-usage__day-flag">{edge}</span>}
          {overlapping.includes(bucket) && <span className="phone-usage__day-flag">prekrýva sa s dennými záznamami</span>}
        </li>
      );
    })}
  </ul>
);

BucketCells.propTypes = {
  app: PropTypes.object.isRequired,
  view: PropTypes.object.isRequired,
  overlapping: PropTypes.array.isRequired,
  id: PropTypes.string,
};

export default class PhoneUsageSnapshot extends Component {
  static propTypes = {
    card: PropTypes.shape({
      loanId: PropTypes.string.isRequired,
      loanNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      status: PropTypes.string,
      snapshotId: PropTypes.string,
      capturedAt: PropTypes.string,
      sourceDevice: PropTypes.object,
    }).isRequired,
    usage: PropTypes.shape({
      status: PropTypes.oneOf(['idle', 'loading', 'ready', 'error']).isRequired,
      snapshot: PropTypes.object,
      audit: PropTypes.array,
      error: PropTypes.object,
    }).isRequired,
    isOpen: PropTypes.bool.isRequired,
    onOpen: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
    onToggleApp: PropTypes.func,
    onSelectView: PropTypes.func,
  };

  static defaultProps = {
    onToggleApp: () => {},
    onSelectView: () => {},
  };

  state = { resolution: 'daily', expanded: [], showAll: false };

  componentDidUpdate(prevProps) {
    const { resolution, expanded, showAll } = this.state;
    const changed = prevProps.card.loanId !== this.props.card.loanId || prevProps.usage.snapshot !== this.props.usage.snapshot;
    if (changed && (resolution !== 'daily' || expanded.length || showAll)) {
      this.setState({ resolution: 'daily', expanded: [], showAll: false });
    }
  }

  selectView = event => {
    const resolution = event.target.value;
    this.setState({ resolution, expanded: [] });
    this.props.onSelectView(resolution);
  };

  toggleApp = identifier => {
    const isExpanded = this.state.expanded.includes(identifier);
    this.setState(state => ({
      expanded: isExpanded
        ? state.expanded.filter(key => key !== identifier)
        : state.expanded.concat(identifier),
    }));
    this.props.onToggleApp(identifier, !isExpanded);
  };

  closeAndRefocus = () => {
    this.props.onClose();
    const openButton = this.section && this.section.querySelector('[data-testid="phone-usage-open"]');
    if (openButton) openButton.focus();
  };

  renderNone() {
    const { status } = this.props.card;
    const reason = NO_SNAPSHOT_REASONS[status] ? status : 'unavailable';
    return (
      <div className="phone-usage__capture" data-testid={`phone-usage-none-${reason}`}>
        <PhoneVisual empty />
        <div className="phone-usage__capture-text">
          <p className="phone-usage__lead">Žiadosť bola odoslaná bez snímky z telefónu.</p>
          <p>{NO_SNAPSHOT_REASONS[status] || NO_SNAPSHOT_FALLBACK}</p>
          <p>Zdieľanie je dobrovoľné; jeho chýbanie sa pri posúdení žiadosti nehodnotí.</p>
        </div>
      </div>
    );
  }

  renderCapture() {
    const { card, isOpen, onOpen } = this.props;
    return (
      <div className="phone-usage__capture" data-testid="phone-usage-capture">
        <PhoneVisual />
        <div className="phone-usage__capture-text">
          <div className="phone-usage__eyebrow" data-testid="phone-usage-captured-on-phone">
            Zachytené v telefóne klienta
          </div>
          <div className="phone-usage__device" data-testid="phone-usage-source">
            {deviceText(card.sourceDevice)}{' '}
            <span className="phone-usage__pill" data-testid="phone-usage-source-relation">
              nezávislé od naposledy aktívneho zariadenia účtu
            </span>
          </div>
          <div className="phone-usage__hint" data-testid="phone-usage-captured">
            {card.capturedAt ? formatDateTimeIn(card.capturedAt) : 'čas zachytenia neuvedený'} · pri odoslaní žiadosti{' '}
            {card.loanNumber}
          </div>
        </div>
        <div className="phone-usage__actions">
          <Button
            type={isOpen ? 'default' : 'primary'}
            aria-expanded={isOpen}
            aria-controls={isOpen ? 'phone-usage-detail' : undefined}
            onClick={isOpen ? this.closeAndRefocus : onOpen}
            data-testid="phone-usage-open"
          >
            {isOpen ? 'Skryť používanie aplikácií' : 'Zobraziť používanie aplikácií'}
          </Button>
          {!isOpen && <span className="phone-usage__hint">Otvorenie sa zapíše do záznamu zobrazení na serveri.</span>}
        </div>
      </div>
    );
  }

  renderMeta(snapshot) {
    const { daily } = snapshot.views;
    const older = OLDER.filter(resolution => snapshot.views[resolution].buckets.length);
    return (
      <div className="phone-usage__meta" data-testid="phone-usage-meta">
        <Field label="Zdroj údajov" testId="phone-usage-history-source">
          {HISTORY_SOURCE[snapshot.source.platform] || platformLabel(snapshot.source.platform)}
          <div className="phone-usage__hint">
            Rozsah vrátenej histórie určuje telefón a nie je zaručený.
          </div>
        </Field>
        <Field label="Vrátená história" testId="phone-usage-returned">
          Denné záznamy: {countLabel(daily.buckets.length, 'daily')}
          <div>
            Staršie súhrny:{' '}
            {older.length ? older.map(resolution => RESOLUTION_COPY[resolution].adjective).join(', ') : 'žiadne'}
          </div>
          <div className="phone-usage__hint">
            pásmo telefónu pri zachytení: {snapshot.timeZone || 'neuvedené'} · časy zobrazené v {DISPLAY_TIME_ZONE};
            pásmo telefónu v starších obdobiach nie je známe
          </div>
        </Field>
        <Field label="Snímka" testId="phone-usage-snapshot-id">
          <span className="phone-usage__mono">{snapshot.id}</span>
          <div className="phone-usage__hint">zachytená {formatDateTimeIn(snapshot.capturedAt)}</div>
        </Field>
        <Field label="Uchovávanie" testId="phone-usage-retention">
          Zostáva pri pôžičke {this.props.card.loanNumber} a zdrojovom zariadení
          <div className="phone-usage__hint">
            Snímka sa nedá zmazať ani nahradiť a automaticky neexpiruje; neskoršie odobratie povolenia v telefóne ju
            nemení.
          </div>
        </Field>
      </div>
    );
  }

  renderNav(snapshot) {
    return (
      <div className="phone-usage__history-nav">
        <span className="phone-usage__label" id="phone-usage-history-label">
          História v snímke
        </span>
        <div role="group" aria-labelledby="phone-usage-history-label" data-testid="phone-usage-history-nav">
          <RadioGroup name="phone-usage-history" value={this.state.resolution} onChange={this.selectView}>
            {RESOLUTIONS.map(resolution => {
              const count = snapshot.views[resolution].buckets.length;
              return (
                <RadioButton key={resolution} value={resolution}>
                  <span data-testid={`phone-usage-history-view-${resolution}`}>
                    {RESOLUTION_COPY[resolution].tab} · {count ? countLabel(count, resolution) : 'nevrátené'}
                  </span>
                </RadioButton>
              );
            })}
          </RadioGroup>
        </div>
        <p className="phone-usage__hint" data-testid="phone-usage-history-hint">
          Zobrazenia sa časovo prekrývajú, preto sa nesčítavajú. Súčty platia iba pre obdobie zobrazené nižšie.
        </p>
      </div>
    );
  }

  renderViewMeta(view, summary) {
    const edges = summary.edgeBuckets.map(bucket => `${bucketLabel(view.resolution, bucket)}: ${bucketEdgeLabel(bucket)}`);
    return (
      <div className="phone-usage__meta" data-testid="phone-usage-view-meta">
        <Field label="Obdobie" testId="phone-usage-period">
          {formatRange(viewRange(view))}
        </Field>
        <Field label="Rozlíšenie" testId="phone-usage-resolution">
          {RESOLUTION_COPY[view.resolution].tab}
          <div className="phone-usage__hint">{RESOLUTION_COPY[view.resolution].detail}</div>
        </Field>
        <Field label="Pokrytie" testId="phone-usage-coverage">
          <span className={summary.isPartial ? 'phone-usage__pill phone-usage__pill--partial' : 'phone-usage__pill'}>
            {summary.isPartial ? 'Čiastočné: ' : ''}
            {countLabel(summary.bucketCount, view.resolution)}
            {summary.isPartial ? `, ${summary.gaps.length} ${summary.gaps.length === 1 ? 'medzera' : 'medzery'}` : ' bez medzier'}
          </span>
          {edges.map(edge => (
            <div className="phone-usage__hint" key={edge}>
              {edge}
            </div>
          ))}
        </Field>
      </div>
    );
  }

  renderEmptyView(view) {
    return (
      <div className="phone-usage__hint" role="note" data-testid="phone-usage-history-empty">
        <p className="phone-usage__lead">Telefón v tejto snímke nevrátil {RESOLUTION_COPY[view.resolution].tab.toLowerCase()}.</p>
        <p>Chýbajúci súhrn neznamená nulové používanie.</p>
      </div>
    );
  }

  renderTable(view, summary, overlapping) {
    const copy = RESOLUTION_COPY[view.resolution];
    const rows = this.state.showAll ? summary.apps : summary.apps.slice(0, VISIBLE_APPS);
    const columns = [
      {
        title: 'Aplikácia',
        key: 'app',
        render: (text, app) => {
          const label = appLabel(app);
          return (
            <div className="phone-usage__app">
              <div className={label.hasName ? 'phone-usage__app-name' : 'phone-usage__app-name phone-usage__mono'}>
                {label.primary}
              </div>
              {label.hasName ? (
                <div className="phone-usage__mono phone-usage__hint">{label.identifier}</div>
              ) : (
                <div className="phone-usage__hint">názov nebol dostupný, zobrazený je identifikátor</div>
              )}
            </div>
          );
        },
      },
      {
        title: summary.additive ? `Spolu za ${countLabel(summary.bucketCount, view.resolution)}` : 'Spolu',
        key: 'total',
        className: 'phone-usage__total-col',
        render: (text, app) => (
          <div data-testid={`phone-usage-total-${app.identifier}`}>
            {summary.additive ? (
              <div>{formatDuration(app.total.durationMs)}</div>
            ) : (
              <div className="phone-usage__hint">nesčítava sa, obdobia sa prekrývajú</div>
            )}
            {app.total.isPartial && (
              <div className="phone-usage__hint">
                čiastočné · {ofLabel(app.total.knownBuckets, summary.bucketCount, view.resolution)}
              </div>
            )}
          </div>
        ),
      },
      {
        title: copy.by,
        key: 'buckets',
        className: 'phone-usage__toggle-col',
        render: (text, app) => {
          const isExpanded = this.state.expanded.includes(app.identifier);
          return (
            <Button
              size="small"
              className="phone-usage__toggle"
              aria-expanded={isExpanded}
              aria-controls={isExpanded ? `phone-usage-buckets-${app.identifier}` : undefined}
              aria-label={`${isExpanded ? 'Skryť' : 'Zobraziť'} ${copy.adjective} hodnoty: ${appLabel(app).primary}`}
              onClick={() => this.toggleApp(app.identifier)}
              data-testid={`phone-usage-toggle-${app.identifier}`}
            >
              {isExpanded ? 'Skryť' : 'Zobraziť'}
            </Button>
          );
        },
      },
    ];

    return (
      <div data-testid="phone-usage-table" data-resolution={view.resolution}>
        <Table
          size="small"
          rowKey="identifier"
          columns={columns}
          dataSource={rows}
          pagination={false}
          expandIconAsCell={false}
          expandIconColumnIndex={-1}
          expandedRowKeys={this.state.expanded}
          expandedRowRender={app => (
            <BucketCells app={app} view={view} overlapping={overlapping} id={`phone-usage-buckets-${app.identifier}`} />
          )}
          footer={
            summary.additive
              ? () => (
                  <span data-testid="phone-usage-view-total">
                    Spolu všetky aplikácie iba za toto zobrazenie ({formatRange(viewRange(view))}):{' '}
                    {formatDuration(summary.totalMs)}
                    {summary.isPartial && ' (čiastočné, s medzerami v histórii)'}
                  </span>
                )
              : undefined
          }
        />
        {summary.apps.length > VISIBLE_APPS && (
          <Button
            size="small"
            className="phone-usage__more"
            onClick={() => this.setState(state => ({ showAll: !state.showAll }))}
            data-testid="phone-usage-show-all"
          >
            {this.state.showAll ? `Zobraziť prvých ${VISIBLE_APPS}` : `Zobraziť všetky aplikácie (${summary.apps.length})`}
          </Button>
        )}
      </div>
    );
  }

  renderHistory(snapshot) {
    const view = snapshot.views[this.state.resolution];
    const summary = summarizeView(view);
    const overlapping = view.resolution === 'daily' ? [] : overlappingBuckets(view, snapshot.views.daily);
    return (
      <section className="phone-usage__history" data-testid="phone-usage-history" aria-labelledby="phone-usage-history-label">
        {this.renderNav(snapshot)}
        <div data-testid="phone-usage-history-panel" data-resolution={view.resolution}>
          {view.buckets.length ? (
            <div>
              {this.renderViewMeta(view, summary)}
              {this.renderTable(view, summary, overlapping)}
            </div>
          ) : (
            this.renderEmptyView(view)
          )}
        </div>
      </section>
    );
  }

  renderDetailBody() {
    const { usage, onRetry } = this.props;
    if (usage.status === 'error') {
      return (
        <div className="phone-usage__status" role="alert" data-testid="phone-usage-error">
          <span>{errorText(usage.error)} Náhradné ani ukážkové údaje sa nezobrazujú.</span>{' '}
          <Button size="small" onClick={onRetry} data-testid="phone-usage-retry">
            Skúsiť znova
          </Button>
        </div>
      );
    }
    if (usage.status !== 'ready') {
      return (
        <div className="phone-usage__status" role="status" data-testid="phone-usage-loading">
          <Spin size="small" /> <span>Načítavam snímku zo servera…</span>
        </div>
      );
    }
    if (!usage.snapshot) {
      return (
        <Note testId="phone-usage-snapshot-missing">
          Server pre túto žiadosť snímku nevrátil. Nič sa nezobrazuje ani nedopĺňa.
        </Note>
      );
    }
    return (
      <div>
        {this.renderMeta(usage.snapshot)}
        <p className="phone-usage__caveat">
          Čas v popredí podľa systému telefónu, iba z tohto zariadenia. Bez obsahu, webových stránok a časovej osi.
          Iba na manuálne posúdenie, bez skóre a hodnotenia.
        </p>
        {this.renderHistory(usage.snapshot)}
        <PhoneUsageAuditLog entries={usage.audit} />
      </div>
    );
  }

  renderSnapshot() {
    const { isOpen } = this.props;
    return (
      <div data-testid="phone-usage-snapshot">
        {this.renderCapture()}
        {isOpen && (
          <div
            id="phone-usage-detail"
            className="phone-usage__detail"
            data-testid="phone-usage-detail"
            role="region"
            aria-label="Používanie aplikácií – podrobnosti"
          >
            {this.renderDetailBody()}
            <Button className="phone-usage__close" onClick={this.closeAndRefocus} data-testid="phone-usage-close">
              Skryť používanie aplikácií
            </Button>
          </div>
        )}
      </div>
    );
  }

  render() {
    return (
      <PhoneUsageFrame
        sectionRef={section => {
          this.section = section;
        }}
      >
        {this.props.card.snapshotId ? this.renderSnapshot() : this.renderNone()}
      </PhoneUsageFrame>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/loan-management/components/phone-usage/PhoneUsageSnapshot.js